/**
 * Culians — Recently viewed
 *
 * Two roles in one file (loaded on product + cart templates):
 *
 *   1. TRACK   – on every product page, push the current product
 *                handle onto a localStorage queue (last 12, no
 *                dupes; revisits move the handle back to the front).
 *
 *   2. RENDER  – on the cart page, read the queue and build image-
 *                only cards that match the PDP "View more" slider —
 *                3:4 aspect, hover crossfade between featured_image
 *                and images[1], 0.4s cubic-bezier(0.4, 0, 0.2, 1).
 *
 * Rendering strategy — perceived speed:
 *   The grid is filled with placeholder slots IMMEDIATELY on page
 *   load (no fetch wait), so the section reveals and lays out
 *   instantly. Each placeholder is then replaced by its real card
 *   as that product's /products/{handle}.js fetch resolves. The
 *   user sees the section right away and individual cards pop in
 *   one by one over a few hundred ms, rather than waiting for the
 *   slowest fetch before the whole grid appears.
 *
 * Bag filter:
 *   Products already in the bag are excluded from the queue before
 *   rendering — the bag section above shows them clearly, so
 *   surfacing them again here would be redundant. The point of
 *   Recently viewed is to nudge the customer toward products they
 *   considered but didn't add.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'culians_recently_viewed';
  var MAX_ITEMS = 12;

  function getStored() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function setStored(arr) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch (e) { /* quota or disabled — fine to ignore */ }
  }

  function currentHandle() {
    var m = window.location.pathname.match(/^\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  /* ─── 1. TRACK on product pages ────────────────────────── */

  function trackCurrentProduct() {
    var handle = currentHandle();
    if (!handle) return;

    var list = getStored();
    // Remove existing entry so the revisit moves to the front.
    list = list.filter(function (h) { return h !== handle; });
    list.unshift(handle);
    list = list.slice(0, MAX_ITEMS);
    setStored(list);
  }

  /* ─── Bag-filter helper ────────────────────────────────── */
  /* Reads handles of products currently in the bag from the
   * full-page cart's item links. We do this from the DOM so we
   * don't need an extra /cart.js round-trip. */
  function getBagHandles() {
    var handles = [];
    var links = document.querySelectorAll('.culians-cart__item-image[href]');
    Array.prototype.forEach.call(links, function (a) {
      var m = a.getAttribute('href').match(/^\/products\/([^/?#]+)/);
      if (m) handles.push(m[1]);
    });
    return handles;
  }

  /* ─── Card builders ────────────────────────────────────── */

  function buildPlaceholder(index) {
    var article = document.createElement('article');
    article.className = 'culians-rv__card culians-rv__card--placeholder';
    article.setAttribute('role', 'listitem');
    article.setAttribute('data-rv-index', String(index));

    var wrap = document.createElement('div');
    wrap.className = 'culians-rv__image-wrapper';
    article.appendChild(wrap);

    return article;
  }

  function sizedUrl(url, w) {
    // /products/{handle}.js returns master-size CDN URLs; request a sized
    // rendition so the cards never pull 3840px files.
    if (!url) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + (w || 1600);
  }

  // Same candidates + sizes as the PDP "View more" cards: 33vw on desktop,
  // 50vw on phones (retina picks the 2× rendition).
  var RV_WIDTHS = [400, 800, 1200, 1600, 2000];
  var RV_SIZES = '(min-width: 750px) 33vw, 50vw';

  function srcsetFor(url) {
    return RV_WIDTHS.map(function (w) { return sizedUrl(url, w) + ' ' + w + 'w'; }).join(', ');
  }

  function buildCard(product, index) {
    var img1 = product.featured_image || (product.images && product.images[0]);

    var article = document.createElement('article');
    article.className = 'culians-rv__card';
    article.setAttribute('role', 'listitem');
    article.setAttribute('data-rv-index', String(index));

    var a = document.createElement('a');
    a.className = 'culians-rv__link';
    a.href = product.url;
    a.setAttribute('aria-label', product.title || '');

    var wrap = document.createElement('div');
    wrap.className = 'culians-rv__image-wrapper';

    // One image per product, no hover swap — the PDP "View more" rule
    // (2026-09-09: tap and slide are the only interactions).
    if (img1) {
      var main = document.createElement('img');
      main.className = 'culians-rv__img culians-rv__img--main';
      main.src = sizedUrl(img1, 1200);
      main.srcset = srcsetFor(img1);
      main.sizes = RV_SIZES;
      main.alt = product.title || '';
      main.loading = 'lazy';
      wrap.appendChild(main);
    }

    a.appendChild(wrap);
    article.appendChild(a);
    return article;
  }

  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (r.ok) return r.json();
        // The store no longer has this handle (renamed or deleted product):
        // drop it from the queue so it is never requested again (2026-09-11).
        if (r.status === 404) setStored(getStored().filter(function (h) { return h !== handle; }));
        return null;
      })
      .catch(function () { return null; });
  }

  /* ─── 2. RENDER on the cart page ───────────────────────── */

  function renderRecentlyViewed() {
    var section = document.querySelector('[data-culians-recently-viewed]');
    if (!section) return;
    var grid = section.querySelector('[data-culians-recently-viewed-grid]');
    if (!grid) return;

    var handles = getStored();
    if (handles.length === 0) {
      section.setAttribute('hidden', '');
      return;
    }

    // Exclude products already in the bag — the bag section above
    // shows them. Surfacing them again would be redundant.
    var bagHandles = getBagHandles();
    handles = handles.filter(function (h) {
      return bagHandles.indexOf(h) === -1;
    });

    if (handles.length === 0) {
      section.setAttribute('hidden', '');
      return;
    }

    // Reveal IMMEDIATELY with placeholder slots in place. This is
    // the perceived-speed win — the section paints in the same
    // frame as the rest of the page, and individual cards pop in
    // as their fetches resolve.
    grid.innerHTML = '';
    // data-count drives the View more grid rule (2 products = 2 columns).
    grid.setAttribute('data-count', String(handles.length));
    handles.forEach(function (handle, index) {
      grid.appendChild(buildPlaceholder(index));
    });
    section.removeAttribute('hidden');

    // Fire all fetches in parallel; each replaces its own slot
    // independently so the slowest fetch never blocks the others.
    handles.forEach(function (handle, index) {
      fetchProduct(handle).then(function (product) {
        var slot = grid.querySelector(
          '.culians-rv__card[data-rv-index="' + index + '"]'
        );
        if (!slot) return;

        if (product) {
          slot.replaceWith(buildCard(product, index));
        } else {
          // 404 or fetch failed — remove the slot rather than leave
          // a permanent placeholder.
          slot.remove();
          grid.setAttribute('data-count', String(grid.children.length));
        }
      });
    });
  }

  /* ─── Init ──────────────────────────────────────────────── */

  function init() {
    if (/^\/products\//.test(window.location.pathname)) {
      trackCurrentProduct();
    }
    // The section only exists on the cart template, so this exits
    // cheaply on every other page.
    renderRecentlyViewed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
